// The date-range vocabulary shared by every admin filter strip: the shape, the
// presets, and the pure helpers that turn one into the other. No React here, so
// the semantics are unit-testable on their own and both the CONTROL
// (`date-range-filter.tsx`) and the callers that only need to serialize a range
// into a query string read the same rules.
//
// A range is two INCLUSIVE calendar days, `YYYY-MM-DD`, either side nullable for
// "unbounded". Calendar days rather than instants because that is what an
// operator picks and what a date input emits; the SERVER is what turns a day
// into an instant, and it does so in UTC (`from` at 00:00:00Z, `to` at the
// following midnight, exclusive) so every caller agrees on where a day ends.
// Near midnight in a far-from-UTC zone that can put a row a few hours either
// side of where the picker suggests. That is the documented cost of picking
// days instead of instants, and it is the same convention the rest of the
// console already reads timestamps under.

/** Two inclusive ISO calendar days (`YYYY-MM-DD`); null = no bound that side. */
export interface DateRange {
  /** Inclusive ISO date (YYYY-MM-DD), or null for "no lower bound". */
  from: string | null;
  /** Inclusive ISO date (YYYY-MM-DD), or null for "no upper bound". */
  to: string | null;
}

/** The unbounded range: every row passes. The default on every filter strip. */
export const EMPTY_DATE_RANGE: DateRange = { from: null, to: null };

/** `YYYY-MM-DD`, and a real calendar day. The month/day ceilings are what stop
 *  `2026-13-40` (which `new Date` happily rolls forward into the next year)
 *  from being treated as a filter somebody meant. */
const ISO_DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Whether a string is a well-formed ISO calendar day this module will accept. */
export function isIsoDay(value: unknown): value is string {
  return typeof value === "string" && ISO_DAY.test(value);
}

/** One `Date` as an ISO calendar day in the LOCAL zone.
 *
 *  Deliberately not `toISOString().slice(0, 10)`, which converts to UTC first:
 *  for anyone west of Greenwich that reports tomorrow's date for most of the
 *  evening, so "today" as a preset boundary would silently skip a day's rows. */
export function toIsoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The last `days` CALENDAR days, ending today, inclusive at both ends.
 *
 *  `days` counts the days on screen, so "Last 7 days" spans today plus the six
 *  before it, not today plus seven. The `- 1` is that promise: the same label
 *  used to hand back eight days of buckets, which made every rate computed over
 *  it quietly wrong at the edge. */
export function rangeForDays(days: number, today: Date = new Date()): DateRange {
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return { from: toIsoDay(from), to: toIsoDay(today) };
}

/** The preset rows every date-range control offers, in display order. `days:
 *  null` is "All time", which is the empty range rather than a computed one. */
export const DATE_RANGE_PRESETS: readonly { label: string; days: number | null }[] = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "All time", days: null },
];

/** Whether a range bounds anything at all. Drives the control's "this filter is
 *  narrowing what you are reading" styling, the same way MultiSelect's does. */
export function isDateRangeActive(range: DateRange): boolean {
  return range.from !== null || range.to !== null;
}

/** ISO days compare correctly as plain strings, so no Date parsing is needed. */
export function isInDateRange(day: string, range: DateRange): boolean {
  if (range.from && day < range.from) return false;
  if (range.to && day > range.to) return false;
  return true;
}

/** Drop malformed bounds and put a backwards pair back in order.
 *
 *  Both halves matter at a boundary. A hand-edited URL is the only way a
 *  non-day string reaches a range, and dropping it beats filtering by
 *  nonsense. Swapping is for the control: picking the END first and the START
 *  second is ordinary, and a range that reads `from > to` matches no row at
 *  all, so an honest reading of the two dates the operator chose is better
 *  than an empty table they have to work out how to escape. */
export function normalizeDateRange(range: DateRange): DateRange {
  const from = isIsoDay(range.from) ? range.from : null;
  const to = isIsoDay(range.to) ? range.to : null;
  if (from && to && from > to) return { from: to, to: from };
  return { from, to };
}

/** Which preset a range IS, or null when it is a custom pair (or a stale
 *  preset: yesterday's "Last 7 days" is not today's, and marking it active
 *  would claim the table shows something it does not). */
export function matchingPresetDays(range: DateRange, today: Date = new Date()): number | null {
  if (!isDateRangeActive(range)) return null;
  for (const preset of DATE_RANGE_PRESETS) {
    if (preset.days === null) continue;
    const candidate = rangeForDays(preset.days, today);
    if (candidate.from === range.from && candidate.to === range.to) return preset.days;
  }
  return null;
}

/** `2026-08-16` as `16 Aug 2026`. Month names rather than a numeric format
 *  because the console is read by people on both sides of the day/month
 *  convention, and `08/09` means two different days to them. */
export function formatIsoDay(day: string): string {
  const [year, month, date] = day.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const name = months[Number(month) - 1];
  if (!name || !year || !date) return day;
  return `${Number(date)} ${name} ${year}`;
}

/** The trigger's text: the preset's own label when the range is one, otherwise
 *  the bounds themselves. An open-ended range says which end is open rather
 *  than implying a bound it does not have. */
export function dateRangeSummary(range: DateRange, today: Date = new Date()): string {
  if (!isDateRangeActive(range)) return "All time";
  const preset = matchingPresetDays(range, today);
  if (preset !== null) {
    return DATE_RANGE_PRESETS.find((p) => p.days === preset)?.label ?? "All time";
  }
  if (range.from && range.to) {
    return range.from === range.to
      ? formatIsoDay(range.from)
      : `${formatIsoDay(range.from)} to ${formatIsoDay(range.to)}`;
  }
  return range.from ? `From ${formatIsoDay(range.from)}` : `Until ${formatIsoDay(range.to!)}`;
}
